import { useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import getEventsIndexConfig from 'hb-react/shared/apps/Events/containers/Index/pages/EventsAppSharedIndex/utils/getEventsIndexConfig';
import type {
  EventRegistrationPageProps,
  EventsAppProps,
  EventsAppV1Props,
  EventsAppV2Props,
  EventsDetailsPageProps,
} from 'hb-react/shared/apps/Events/startup/types';
import CombineProvider from 'hb-react/shared/components/CombineProvider';
import { EvErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';
import { ShoppingCardProvider } from 'hb-react/shared/components/ShoppingCard/ShoppingCardContext';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

import EventDetailsContainer from '../components/EventDetailsContainer';
import EventPaymentContainer from '../components/EventPaymentContainer';
import EventRegistrationContainer from '../components/EventRegistrationContainer';
import EventSuccessContainer from '../components/EventSuccessContainer';
import { IndexContainer } from '../containers/Index';
import { DonationsProvider } from '../contexts/DonationsContext';
import { EventCartProvider } from '../contexts/EventCartContext';
import { EventProvider } from '../contexts/EventContext';
import { GuestsProvider } from '../contexts/GuestsContext';
import { RegistrationFlowProvider } from '../contexts/RegistrationFlowContext';
import { useEvent } from '../hooks';
import {
  BillingInformationPage,
  DonationsPage,
  InitPage,
  PaymentPage,
  RegistrationPage,
  SuccessPage,
  TicketSelectionPage,
} from '../pages/Booking';
import { Layout } from '../pages/Booking/components/Layout';

const EventsAppV1 = ({ config, topic }: EventsAppV1Props) => {
  const {
    currentUser,
    currentNetwork: { features: featuresConfig },
  } = useDefaultAppContexts();

  const topicContext = useTopicContext();

  const eventsConfig = useMemo(() => {
    if (topicContext.topicId) {
      return getEventsIndexConfig('group', {
        currentUser,
        featuresConfig,
        topicId: topicContext.topicId,
        topicContext,
      });
    }

    return getEventsIndexConfig(
      'network',
      {
        currentUser,
        featuresConfig,
      },
      config?.custom?.page,
    );
  }, [topicContext, currentUser, featuresConfig, config?.custom?.page]);

  return (
    <EvErrorBoundary>
      <Routes>
        <Route path=":eventId/*">
          <Route index element={<EventDetailsPage config={config} />} />
          <Route
            path="registration"
            element={
              <EventRegistrationPage config={config}>
                <EventRegistrationContainer />
              </EventRegistrationPage>
            }
          />
          <Route
            path="payment"
            element={
              <EventRegistrationPage config={config}>
                <EventPaymentContainer />
              </EventRegistrationPage>
            }
          />
          <Route
            path="success"
            element={
              <EventRegistrationPage config={config}>
                <EventSuccessContainer />
              </EventRegistrationPage>
            }
          />
          <Route path="*" element={<EventDetailsPage config={config} />} />
        </Route>
        <Route
          path="/"
          element={<IndexContainer config={eventsConfig} topic={topic} />}
        >
          <Route
            path="p/:eventId"
            element={<IndexContainer config={eventsConfig} topic={topic} />}
          />
        </Route>
      </Routes>
    </EvErrorBoundary>
  );
};

const EventDetailsPage = ({ config }: EventsDetailsPageProps) => {
  const { isLoading, event } = useEvent({
    route: config.routes.event_path,
  });
  const { currentNetwork } = useDefaultAppContexts();

  if (isLoading) {
    return <Loader />;
  }

  if (!event) {
    return null;
  }

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title: event?.title,
        })}
      />
      <EventProvider event={event} config={config}>
        <EventDetailsContainer />
      </EventProvider>
    </>
  );
};

const EventRegistrationPage = ({
  config,
  children,
}: EventRegistrationPageProps) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const { isLoading, event } = useEvent({
    route: config.routes.event_registration_path,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (!event) {
    return null;
  }

  return (
    <CombineProvider
      providers={[
        { provider: EventProvider, event, config },
        { provider: EventCartProvider, event },
        { provider: RegistrationFlowProvider },
        { provider: ShoppingCardProvider },
        { provider: DonationsProvider },
        ...(currentUser ? [{ provider: GuestsProvider }] : []),
      ]}
    >
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title: event?.title,
        })}
      />
      {children}
    </CombineProvider>
  );
};

const EventsAppV2 = ({ config, topic }: EventsAppV2Props) => {
  const {
    currentUser,
    currentNetwork: { features: featuresConfig },
  } = useDefaultAppContexts();

  const topicContext = useTopicContext();

  const eventsConfig = useMemo(() => {
    if (topicContext.topicId) {
      return getEventsIndexConfig('group', {
        currentUser,
        featuresConfig,
        topicId: topicContext.topicId,
        topicContext,
      });
    }

    return getEventsIndexConfig(
      'network',
      {
        currentUser,
        featuresConfig,
      },
      config?.custom?.page,
    );
  }, [topicContext, currentUser, featuresConfig, config?.custom?.page]);

  return (
    <Routes>
      <Route path=":eventId/*">
        <Route path="*" element={<EventDetailsPage config={config} />} />
        <Route
          path="booking/*"
          element={
            <GuestsProvider>
              <ShoppingCardProvider>
                <Outlet />
              </ShoppingCardProvider>
            </GuestsProvider>
          }
        >
          <Route path=":cartId/*" element={<Layout />}>
            <Route path="ticket-selection/*" element={<TicketSelectionPage />}>
              <Route path="donations" element={<DonationsPage />} />
            </Route>
            <Route path="registration/*" element={<RegistrationPage />}>
              <Route path="donations" element={<DonationsPage />} />
            </Route>
            <Route path="billing/*" element={<BillingInformationPage />}>
              <Route path="donations" element={<DonationsPage />} />
            </Route>
            <Route path="payment" element={<PaymentPage />} />
            <Route path="success" element={<SuccessPage />} />
            <Route path="*" element={<Navigate to="ticket-selection" />} />
          </Route>
          <Route index element={<InitPage />} />
        </Route>
        <Route
          path="registration"
          element={<Navigate to="../booking" replace />}
        />
      </Route>
      <Route
        path="/"
        element={<IndexContainer config={eventsConfig} topic={topic} />}
      >
        <Route
          path="p/:eventId"
          element={<IndexContainer config={eventsConfig} topic={topic} />}
        />
      </Route>
    </Routes>
  );
};

const EventsApp = ({ config, topic }: EventsAppProps) => {
  const { flagEvOmEvents } = useFeatureFlag();

  if (flagEvOmEvents) {
    return <EventsAppV2 config={config} topic={topic} />;
  }

  return <EventsAppV1 config={config} />;
};

export default EventsApp;
